import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

// NOTE: We support three schema variants:
// 1. Employer portal: client + employee implicitly derived from logged-in client admin. No requester selection fields.
// 2. Employee portal: employee context derived from session, targeting client admin. No selection fields needed.
// 3. Broker (and other) portal: original behavior allowing selection/validation.

const buildSchema = (portalType: string) => {
  if (portalType === 'employer' || portalType === 'employee') {
    return z.object({
      title: z.string().min(1, 'Title is required'),
      description: z.string().min(10, 'Description must be at least 10 characters'),
      priority: z.enum(['low', 'medium', 'high', 'urgent']),
      category: z.string().min(1, 'Category is required'),
      tags: z.string().optional(),
      // requesterType fixed based on portal type
      requesterType: z.literal(portalType === 'employer' ? 'client_admin' : 'employee').default(portalType === 'employer' ? 'client_admin' : 'employee'),
      // Kept optional placeholders for API compatibility (ignored when absent)
      clientId: z.string().optional(),
      employeeId: z.string().optional()
    });
  }
  // Original (broker) schema with validation around selecting client/employee
  return z.object({
    title: z.string().min(1, 'Title is required'),
    description: z.string().min(10, 'Description must be at least 10 characters'),
    priority: z.enum(['low', 'medium', 'high', 'urgent']),
    category: z.string().min(1, 'Category is required'),
    tags: z.string().optional(),
    requesterType: z.enum(['client_admin', 'employee', 'dependent']).default('employee'),
    clientId: z.string().optional(),
    employeeId: z.string().optional()
  })
    .refine(data => (data.clientId && data.clientId.trim() !== '') || (data.employeeId && data.employeeId.trim() !== ''), {
      message: 'Select a client or an employee',
      path: ['clientId']
    })
    .refine(data => data.requesterType !== 'employee' || (data.employeeId && data.employeeId.trim() !== ''), {
      message: 'Select an employee when requester type is employee',
      path: ['employeeId']
    });
};

type TicketFormData = z.infer<ReturnType<typeof buildSchema>>;

interface TaskSubmissionFormProps {
  portalType: "employer" | "employee" | "broker";
}

export default function TaskSubmissionForm({ portalType }: TaskSubmissionFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const schema = useMemo(() => buildSchema(portalType), [portalType]);

  const form = useForm<TicketFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      priority: 'medium',
      category: '',
      tags: '',
      requesterType: portalType === 'employer' ? 'client_admin' : 'employee',
      clientId: '',
      employeeId: ''
    }
  });

  // Fetch clients (for dropdown selection - only for broker portal)
  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ["/api/clients"],
    enabled: portalType === 'broker' // only broker portal asks for client selection
  });

  // Fetch employees for selected client (broker portal only)
  const selectedClientId = form.watch('clientId');
  const { data: employees = [] } = useQuery({
    queryKey: ['/api/employees', { clientId: selectedClientId || undefined }],
    enabled: portalType === 'broker' && !!selectedClientId,
    queryFn: async ({ queryKey }) => {
      const [_key, params] = queryKey as [string, any];
      const url = new URL('/api/employees', window.location.origin);
      if (params.clientId) url.searchParams.set('clientId', params.clientId);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load employees');
      return res.json();
    }
  });

  // Create ticket mutation
  const createTicketMutation = useMutation({
    mutationFn: async (data: TicketFormData) => {
      // Broker portal can attribute the ticket to a selected client/employee.
      // Employee/employer portals leave requester identity to the server session
      // (no client-side placeholders).
      const client = portalType === 'broker' ? clients.find((c: any) => String(c.id) === data.clientId) : undefined;
      const employee = portalType === 'broker' ? employees.find((e: any) => String(e.id) === data.employeeId) : undefined;

      if (portalType === 'broker' && data.requesterType === 'employee' && !employee) {
        throw new Error('Employee requester type requires selecting an employee.');
      }

      const requesterType =
        portalType === 'employer' ? 'client_admin'
        : portalType === 'employee' ? 'employee'
        : (employee ? 'employee' : data.requesterType);

      // Broker: real requester from the selected employee/client (omitted when
      // unknown — never a placeholder). Other portals send no name/email.
      const brokerRequester = portalType === 'broker'
        ? {
            requesterName: employee
              ? `${employee.firstName} ${employee.lastName}`
              : (client?.primaryContactName || client?.name || undefined),
            requesterEmail: employee ? employee.email : (client?.contactEmail || undefined),
          }
        : {};

      const ticketData = {
        title: data.title,
        description: data.description,
        priority: data.priority,
        category: data.category,
        tags: data.tags ? data.tags.split(",").map(tag => tag.trim()) : [],
        clientId: portalType === 'broker' ? (data.clientId ? parseInt(data.clientId) : undefined) : undefined,
        employeeId: portalType === 'broker' ? (data.employeeId ? parseInt(data.employeeId) : undefined) : undefined,
        ...brokerRequester,
        requesterType,
        submittedFrom: portalType,
      };
      return apiRequest('POST', '/api/tasks', ticketData);
    },
    onSuccess: (data) => {
      console.log("[TASK FORM] Task created successfully:", data);
      const description = portalType === 'employee' 
        ? "Your support request has been sent to your employer's HR team for review and resolution."
        : "Your support request has been routed to the broker team for review and resolution.";
        
      toast({
        title: "Task Submitted",
        description,
      });
      setIsSubmitted(true);
      form.reset();
      console.log("[TASK FORM] Invalidating queries...");
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      // Also invalidate employee queries specifically
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me"] });
    },
    onError: (error: any) => {
      toast({
        title: "Submission Failed",
        description: "Failed to submit task. Please try again.",
        variant: "destructive",
      });
    }
  });

  const onSubmit = (data: TicketFormData) => {
    createTicketMutation.mutate(data);
  };

  // Categories based on portal type
  const categories = portalType === "employer" 
    ? [
        "Employee Support Request",
        "System Access Issue",
        "Payroll Problem",
        "Benefits Question", 
        "Compliance Issue",
        "HR Policy Question",
        "Technical Support",
        "Account Management",
        "Other"
      ]
    : portalType === "employee"
    ? [
        "Benefits Enrollment",
        "Benefits Question",
        "Payroll Issue",
        "PTO Request",
        "System Access",
        "Personal Information Update",
        "Document Request",
        "Technical Support",
        "Other"
      ]
    : [
        "Benefits Enrollment",
        "Plan Changes",
        "Claims",
        "Billing",
        "General"
      ];

  if (isSubmitted) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
          <h3 className="text-xl font-semibold mb-2">Task Submitted Successfully!</h3>
          <p className="text-muted-foreground text-center mb-6">
            {portalType === 'employee' 
              ? "Your support request has been sent to your employer's HR team. You'll receive updates via email as they work on your request."
              : "Your support request has been routed to the broker team. You'll receive updates via email as they work on your request."
            }
          </p>
          <Button onClick={() => setIsSubmitted(false)} variant="outline">
            Submit Another Task
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {portalType === "employer" 
            ? "Submit HR Support Request" 
            : portalType === "employee"
            ? "Submit Support Request to HR"
            : "Submit Support Request"
          }
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Brief description of the issue"
                {...form.register("title")}
              />
              {form.formState.errors.title && (
                <p className="text-sm text-red-500">{form.formState.errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={form.watch("priority")} onValueChange={(value) => form.setValue("priority", value as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select value={form.watch("category")} onValueChange={(value) => form.setValue("category", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.category && (
              <p className="text-sm text-red-500">{form.formState.errors.category.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Please provide detailed information about your request or issue..."
              rows={6}
              {...form.register("description")}
            />
            {form.formState.errors.description && (
              <p className="text-sm text-red-500">{form.formState.errors.description.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags (Optional)</Label>
            <Input
              id="tags"
              placeholder="Comma-separated tags (e.g., urgent, benefits, payroll)"
              {...form.register("tags")}
            />
            <p className="text-sm text-muted-foreground">
              Add tags to help categorize and prioritize your request
            </p>
          </div>

          <div className="flex gap-4">
            <Button 
              type="submit" 
              disabled={createTicketMutation.isPending}
              className="flex-1"
            >
              {createTicketMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Submit Ticket
            </Button>
            <Button type="button" variant="outline" onClick={() => form.reset()}>
              Clear Form
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}